import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { MatTableDataSource } from '@angular/material/table'
import {
  combineLatest,
  map,
  Observable,
  startWith,
  Subject,
  takeUntil
} from 'rxjs'
import { FoodItemModel } from 'src/app/models/food-item.model'
import { AyBService } from 'src/app/services/api/ayb.service'
import Swal from 'sweetalert2'
import { FormCocinaService } from '../../../services/forms/form-cocina.service'

@Component({
  selector: 'app-dialog-agregar-insumo',
  templateUrl: './dialog-agregar-insumo.component.html',
  styleUrls: ['./dialog-agregar-insumo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogAgregarInsumoComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<boolean>()
  private readonly loading$ = new Subject<boolean>()

  isLoading = true
  insumoForm!: FormGroup
  searchControl = new FormControl<string | any>('')

  allSkus: FoodItemModel[] = []
  dishIngredients: FoodItemModel[] = []
  addedInsumos: FoodItemModel[] = []

  displayedColumns = ['nombreInsumo', 'cantidad', 'unidadMedida']
  dataSource = new MatTableDataSource(this.addedInsumos)

  get isLoadingInsumosFromDish$ () {
    return this.loading$.asObservable()
  }

  filteredOptionsWithValidation$!: Observable<
    { option: FoodItemModel; isDuplicate: boolean }[]
  >

  constructor (
    private readonly cdRef: ChangeDetectorRef,
    private readonly aybService: AyBService,
    private readonly formService: FormCocinaService,
    public dialogAgregarInsumoComponentRef: MatDialogRef<DialogAgregarInsumoComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      platoId: string
    }
  ) {}

  ngOnInit (): void {
    this.isLoading = true
    this.loading$.next(true)
    this.insumoForm = this.formService.getInsumoForm()

    combineLatest([
      this.aybService.queryGetDishById({ dishId: this.data?.platoId ?? '' }),
      this.aybService.queryGetAllFoodItemsSku()
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([dishResponse, skusResponse]) => {
          this.dishIngredients = dishResponse?.getDishById?.insumos || []
          this.allSkus = skusResponse?.getAllFoodItemsSku?.insumos || []
          this._initializeFilteredOptions()
          this.isLoading = false
          this.loading$.next(false)
          this.cdRef.markForCheck()
        },
        error: err => {
          console.error(err)
          this.isLoading = false
          this.loading$.next(false)
          this.cdRef.markForCheck()
          this._fireNotificationMessage({
            icon: 'error',
            message: 'Error al cargar los datos del plato.'
          })
        }
      })
  }

  ngOnDestroy (): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  updatePlato () {
    if (this.addedInsumos.length > 0) {
      const plato = { insumos: this.dishIngredients }
      this.aybService
        .mutationUpdatePlato({ platoId: this.data?.platoId || '', plato })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: updatePlatoResponse => {
            console.log({ updatePlatoResponse })
            this.dialogAgregarInsumoComponentRef.close(1)
          },
          error: err => {
            console.error(err)
            this._fireNotificationMessage({
              message: 'Error al agregar insumos al plato.',
              icon: 'error'
            })
          }
        })
    } else {
      this._fireNotificationMessage({
        message: 'No ha agregado insumos al plato',
        icon: 'warning'
      })
    }
  }

  addInsumo () {
    const selectedInsumo = this.searchControl.value
    const cantidad = this.insumoForm.value.cantidad
    const unidadMedida = this.insumoForm.value.unidadMedida

    if (!selectedInsumo || !cantidad || !unidadMedida) {
      this._fireNotificationMessage({
        message: 'Falta ingresar campos requeridos',
        icon: 'warning'
      })
      return
    }

    if (
      this.dishIngredients.some(
        ingredient => ingredient.id === selectedInsumo.id
      )
    ) {
      this._fireNotificationMessage({
        message: `Este ingrediente ya existe en el plato: ${selectedInsumo.nombreInsumo}`,
        icon: 'error'
      })
      return
    }

    const newInsumo = {
      id: selectedInsumo.id,
      nombreInsumo: selectedInsumo.nombreInsumo,
      unidadMedida,
      cantidad
    } as FoodItemModel & { cantidad: number }

    this.addedInsumos.push(newInsumo)
    this.dishIngredients.push(newInsumo)
    this.dataSource = new MatTableDataSource(this.addedInsumos)

    this._initializeFilteredOptions()

    this.insumoForm.reset()
    this.searchControl.reset()
    this.cdRef.markForCheck()
  }

  getOptionText (option: any): string {
    return option?.nombreInsumo || ''
  }

  updateInsumoUnidadMedida (event: MatAutocompleteSelectedEvent): void {
    this.insumoForm
      .get('unidadMedida')
      ?.setValue(event.option.value.unidadMedida)
  }

  private _filterSkus (searchTerm: string): FoodItemModel[] {
    if (!searchTerm) return this.allSkus

    const filterValue = searchTerm.toLowerCase()
    return this.allSkus.filter(sku =>
      sku.nombreInsumo?.toLowerCase().includes(filterValue)
    )
  }

  private _initializeFilteredOptions (): void {
    this.filteredOptionsWithValidation$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      map(searchValue => {
        const searchTerm =
          typeof searchValue === 'string'
            ? searchValue
            : searchValue?.nombreInsumo || ''
        const filteredSkus = this._filterSkus(searchTerm)

        return filteredSkus.map(sku => ({
          option: sku,
          isDuplicate: this.dishIngredients.some(
            ingredient => ingredient.id === sku.id
          )
        }))
      })
    )
  }

  private _fireNotificationMessage ({
    message,
    icon
  }: {
    message: string
    icon: 'error' | 'warning'
  }): void {
    Swal.fire({
      title: message,
      toast: true,
      position: 'top-end',
      icon,
      showConfirmButton: false,
      timer: 3000
    })
  }
}
